import type { ReactNode } from 'react';
import { Button } from '@viquard/doc-ui/components';
import type {
  CalendarEventStatus,
  CalendarGroup,
  CalendarKindMap,
} from '../types.js';
import { useCalendarMessages } from '../i18n.js';
import { MultiKindFilter } from './MultiKindFilter.js';
import { MoreFiltersButton } from './MoreFiltersButton.js';

export interface FilterBarProps {
  readonly kindTheme: CalendarKindMap;
  readonly visibleKinds?: ReadonlySet<string> | undefined;
  readonly groups?: readonly CalendarGroup[] | undefined;

  readonly kindValue: ReadonlySet<string>;
  readonly onKindChange: (next: Set<string>) => void;

  readonly groupValue: ReadonlySet<string>;
  readonly onGroupChange: (next: Set<string>) => void;

  readonly statusValue: ReadonlySet<CalendarEventStatus>;
  readonly onStatusChange: (next: Set<CalendarEventStatus>) => void;

  readonly mobile?: boolean | undefined;
  readonly showGroupFilter?: boolean | undefined;
}

/**
 * Two-tier filter UX (matches DEMO/calendar.jsx kind-strip layout):
 *
 *   [All] [● Publish] [● Newsletter] [● Survey] ...   [Filters · 2]   [Reset]
 *   └─ primary, always-visible kind chips ─┘          └ secondary ─┘
 *
 * Why kinds are inline and groups/status are tucked into a single
 * "Filters" button:
 *
 * - Kind is the most-used filter, so every option is always visible
 *   (one tap to slice). On mobile the strip horizontally scrolls if
 *   needed via scroll-snap, never hiding kinds behind a chevron.
 *
 * - Group + Status are secondary in everyday use; combining them under
 *   one trigger keeps the filter row scannable on a 380px phone. The
 *   trigger badges the active facet count so users know there's
 *   something tucked away.
 *
 * "Reset" only appears when at least one facet has a non-default
 * value, so the chrome stays clean by default.
 */
export function FilterBar(props: FilterBarProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    kindTheme,
    visibleKinds,
    groups,
    kindValue,
    onKindChange,
    groupValue,
    onGroupChange,
    statusValue,
    onStatusChange,
    mobile = false,
    showGroupFilter = true,
  } = props;

  const showGroups =
    showGroupFilter && groups !== undefined && groups.length > 0;
  const anyActive = kindValue.size > 0 || groupValue.size > 0 || statusValue.size > 0;

  const reset = (): void => {
    onKindChange(new Set());
    onGroupChange(new Set());
    onStatusChange(new Set());
  };

  return (
    <div
      className={[
        'vq-cal__filter-bar',
        mobile ? 'is-mobile' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="toolbar"
      aria-label={messages.filtersAriaLabel}
    >
      <MultiKindFilter
        kindTheme={kindTheme}
        value={kindValue}
        onChange={onKindChange}
        visibleKinds={visibleKinds}
        size={mobile ? 'md' : 'sm'}
      />
      <div className="vq-cal__filter-bar-secondary">
        <MoreFiltersButton
          {...(showGroups ? { groups } : {})}
          groupValue={groupValue}
          onGroupChange={onGroupChange}
          statusValue={statusValue}
          onStatusChange={onStatusChange}
          mobile={mobile}
          size={mobile ? 'md' : 'sm'}
        />
        {anyActive && (
          <Button
            variant="ghost"
            size={mobile ? 'md' : 'sm'}
            shape="pill"
            onClick={reset}
          >
            {messages.resetFilters}
          </Button>
        )}
      </div>
    </div>
  );
}
